'use client';

import { useState, useRef, useCallback, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { Chat, MessageRef } from '@/components/chat';

interface HomePageClientProps {
  initialConversationId: string | null;
}

const HERO_REPORT_AVATARS = [
  '/assets/hero-avatars/hero_avatar-1.jpg',
  '/assets/hero-avatars/hero_avatar-2.jpg',
  '/assets/hero-avatars/hero_avatar-3.jpg',
  '/assets/hero-avatars/hero_avatar-4.jpg',
  '/assets/hero-avatars/hero_avatar-5.jpg',
] as const;

const LoadingScreen = () => (
  <div
    className="min-h-screen bg-lawmingo-gray flex items-center justify-center"
    style={{
      backgroundImage:
        'url(https://lawmingo.eu/app/uploads/2023/07/consultant3-section-bg2.webp)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
    }}
  >
    <div className="text-center">
      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-accent-600 mx-auto"></div>
      <p className="mt-4 text-lawmingo-cream/70">Inicjalizacja rozmowy...</p>
    </div>
  </div>
);

export default function HomePageClient({
  initialConversationId,
}: HomePageClientProps) {
  const router = useRouter();
  const [conversationId, setConversationId] = useState<string | null>(
    initialConversationId
  );
  const [checkingCookie, setCheckingCookie] = useState(!initialConversationId);
  const checkedRef = useRef(false);
  const messageRefs = useRef<Map<number, MessageRef>>(new Map());
  const [quoteProgress, setQuoteProgress] = useState(5);

  useEffect(() => {
    if (checkedRef.current || initialConversationId) return;
    checkedRef.current = true;

    const cookies = document.cookie.split(';');
    const conversationCookie = cookies.find((c) =>
      c.trim().startsWith('conversation_id=')
    );

    if (conversationCookie) {
      const existingConversationId = conversationCookie.split('=')[1];
      router.replace(`/chat/${existingConversationId}`);
    } else {
      setCheckingCookie(false);
    }
  }, [router, initialConversationId]);

  const handleConversationCreated = useCallback((newId: string) => {
    setConversationId(newId);
  }, []);

  if (checkingCookie) {
    return <LoadingScreen />;
  }

  return (
    <div
      className="bg-lawmingo-gray flex flex-col"
      style={{
        backgroundImage:
          'url(https://lawmingo.eu/app/uploads/2023/07/consultant3-section-bg2.webp)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      {/* Header */}
      <div className="sticky top-0 z-20 bg-lawmingo-charcoal shadow-lawmingo">
        <div className="px-6 py-4 border-b border-lawmingo-gray/30">
          <div className="flex items-center justify-between gap-x-4">
            <div className="flex items-center gap-x-4 space-x-4">
              <a
                href="https://wycenasprawybiznes.lawmingo.eu/"
                className="text-[32px] md:text-[40px] font-display font-normal text-accent-600 hover:underline focus:underline"
                style={{ textDecoration: 'none' }}
              >
                Lawmingo
              </a>
            </div>
            <a
              href="tel:+48531051513"
              className="bg-accent-600 text-lawmingo-charcoal font-medium px-6 py-2.5 rounded-button hover:bg-[#f4af23] transition-colors"
            >
              Zadzwoń
            </a>
          </div>
        </div>
        {/* Progress bar - sticky on mobile only */}
        <div
          className="lg:hidden w-full h-[20px] border-t border-b border-[#F5CB5C] bg-[#242424] relative"
          role="progressbar"
          aria-valuenow={quoteProgress}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Postęp wyceny"
        >
          <div
            className="absolute left-0 top-0 bottom-0 bg-[#F5CB5C]"
            style={{
              width: `${quoteProgress}%`,
              borderRadius: quoteProgress === 100 ? '0' : '0 50px 50px 0',
              transition: 'width 0.5s ease-in-out',
            }}
          />
        </div>
      </div>

      {/* Main content wrapper */}
      <div id="chat-hero" className="flex-1 flex flex-col">
        {/* Chat and right panel */}
        <div
          className="flex flex-col lg:flex-row lg:max-h-[720px] lg:h-[100vh]"
          style={{
            backgroundImage:
              'url(https://lawmingo.eu/app/uploads/2023/07/consultant3-section-bg2.webp)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        >
          {/* Left column - Chat */}
          <div className="min-h-[60vh] h-[80vh] lg:max-h-[720px] lg:flex-1 flex flex-col relative overflow-hidden">
            {/* Progress bar - desktop only (not sticky) */}
            <div
              className="hidden lg:block w-full h-[20px] border-t border-b border-[#F5CB5C] bg-[#242424] relative flex-shrink-0"
              role="progressbar"
              aria-valuenow={quoteProgress}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="Postęp wyceny"
            >
              <div
                className="absolute left-0 top-0 bottom-0 bg-[#F5CB5C]"
                style={{
                  width: `${quoteProgress}%`,
                  borderRadius: quoteProgress === 100 ? '0' : '0 50px 50px 0',
                  transition: 'width 0.5s ease-in-out',
                }}
              />
            </div>
            <div className="flex-1 flex relative overflow-hidden">
              <Chat
                conversationId={conversationId}
                messageRefs={messageRefs}
                setQuoteProgress={setQuoteProgress}
                onConversationCreated={handleConversationCreated}
              />
            </div>
          </div>

          {/* Right column - Gradient and image (1/3) */}
          <div
            className="w-full lg:w-1/3 lg:max-w-[493px] flex flex-col justify-center py-8 px-4 items-center min-h-[390px] h-full min-h-0"
            style={{
              background:
                'radial-gradient(circle at bottom right, #F8D77D 0%, #CFDBD5 100%)',
            }}
          >
            <div className="w-full max-w-[493px] flex flex-col items-center h-full min-h-0">
              <div className="relative mb-6 flex-1 flex items-center justify-center min-h-0">
                <img
                  src="/assets/mockup_biznes.svg"
                  alt="Lawmingo Banner"
                  className="max-w-full max-h-full h-auto w-auto object-contain rounded-card mx-auto max-w-full lg:max-w-[540px] lg:ml-[-12%] z-10"
                  style={{ maxHeight: '100%' }}
                />
              </div>
              <div className="w-full">
                <h3 className="text-[28px] font-display text-lawmingo-charcoal mb-8 text-center leading-tight">
                  <span className="font-bold">Otrzymaj bezpłatnie</span>
                  <br />
                  profesjonalny raport z wyceną
                </h3>
              </div>
              <div className="w-full">
                <div className="w-full flex justify-center mb-6">
                  <div className="w-full">
                    <div className="grid grid-cols-[1.4fr_0.6fr] items-center gap-4 rounded-[72px] border border-[#FFB900] bg-[#333333]">
                      <div className="bg-[#232323] rounded-[85px] px-[20px] py-[18px] flex items-center justify-start">
                        <span className="font-dmsans font-semibold text-[12px] leading-[140%] tracking-[0] text-white align-middle">
                          Ponad 200 osób tygodniowo otrzymuje nasze raporty
                          wycen
                        </span>
                      </div>
                      <div className="flex justify-end px-[18px] py-[18px]">
                        <div className="flex items-center">
                          {HERO_REPORT_AVATARS.map((src, idx) => (
                            <div
                              key={src}
                              className={`h-[42px] w-[42px] lg:h-[32px] lg:w-[32px] xl:h-[42px] xl:w-[42px] rounded-full border-2 border-[#262626] overflow-hidden ${idx !== 0 ? '-ml-3' : ''}`}
                            >
                              <img
                                src={src}
                                alt={`Klient ${idx + 1}`}
                                className="h-full w-full object-cover"
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <p className="text-[9px] font-sans text-lawmingo-charcoal/70 leading-tight text-left">
                  Warunkiem uzyskania bezpłatnej wyceny jest udzielenie
                  aktywnych zgód marketingowych. Wycena zostanie przygotowana
                  przez Lawmingo.pl na zasadach określonych w
                  <a
                    href="/assets/Lawmingo_regulamin_uslugi.pdf"
                    className="underline ml-1"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    regulaminie Serwisu
                  </a>
                  . Wycena ma charakter informacyjny, nie stanowi wiążącej
                  rekomendacji. Ta strona jest chroniona przez reCAPTCHA i
                  obowiązuje na niej
                  <a
                    href="/assets/Lawmingo_polityka_prywatnosci.pdf"
                    className="underline ml-1"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Polityka Prywatności
                  </a>{' '}
                  Google oraz{' '}
                  <a href="#" className="underline">
                    Warunki korzystania
                  </a>{' '}
                  z usługi.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
